import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, MessageSquare, Plus } from 'lucide-react';

import { listEmployees } from '@/api/employees.api';
import {
  cancelOneOnOne,
  completeOneOnOne,
  createOneOnOne,
  getQuarterlyContext,
  listOneOnOnes,
  updateOneOnOne,
  type ConversationAgenda,
  type CoreValueRating,
  type OneOnOneKind,
  type OneOnOneStatus,
  type OneOnOneWithParticipants,
  type QuarterlyContext,
} from '@/api/eos/oneOnOnes.api';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { describeEosError } from '@/lib/eos/errors';
import { isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * EOS 1-on-1s — Stream B Wave 3 (ADR-023).
 *
 * Manager ↔ report check-ins and quarterly conversations. Privacy
 * (locked): each 1-on-1 is visible to its two participants (superadmins
 * see all); notes and ratings are split per side and each participant
 * edits ONLY their own — the server rejects foreign fields with
 * `field_not_yours`.
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

const TEXTAREA_CLS =
  'w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20 read-only:bg-kw-bg';

const KIND_META: Record<OneOnOneKind, { label: string; variant: BadgeProps['variant'] }> = {
  check_in: { label: 'Check-in', variant: 'info' },
  quarterly: { label: 'Quarterly', variant: 'neutral' },
  annual: { label: 'Annual review', variant: 'warning' },
};

/** Kinds that carry a structured EOS 5-5-5 agenda. */
function isConversationKind(kind: OneOnOneKind): boolean {
  return kind === 'quarterly' || kind === 'annual';
}

/** The +/~/− core-value ratings, in display order. */
const VALUE_RATINGS: { value: CoreValueRating; symbol: string; label: string }[] = [
  { value: 'plus', symbol: '+', label: 'Living it' },
  { value: 'plus_minus', symbol: '~', label: 'Sometimes' },
  { value: 'minus', symbol: '−', label: 'Not living it' },
];

const STATUS_META: Record<OneOnOneStatus, { label: string; variant: BadgeProps['variant'] }> = {
  scheduled: { label: 'Scheduled', variant: 'neutral' },
  completed: { label: 'Completed', variant: 'success' },
  canceled: { label: 'Canceled', variant: 'warning' },
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fullName(m: { user: { firstName: string; lastName: string } }): string {
  return `${m.user.firstName} ${m.user.lastName}`;
}

export function OneOnOnesPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const myMembershipId = me?.membership.id ?? null;
  const amSuperadmin = me !== null && isSuperadmin(me);
  const canSchedule = amSuperadmin || me?.membership.role === 'department_lead';

  const [scheduleOpen, setScheduleOpen] = useState(false);

  const listQuery = useQuery({ queryKey: ['eos', 'one-on-ones'], queryFn: () => listOneOnOnes() });

  const rows = useMemo(() => listQuery.data ?? [], [listQuery.data]);
  const upcoming = useMemo(() => rows.filter((r) => r.status === 'scheduled'), [rows]);
  const past = useMemo(
    () =>
      rows
        .filter((r) => r.status !== 'scheduled')
        .sort((a, b) => new Date(b.scheduledFor).valueOf() - new Date(a.scheduledFor).valueOf()),
    [rows],
  );

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'one-on-ones'] });
  }

  function renderRow(row: OneOnOneWithParticipants) {
    return (
      <OneOnOneCard
        key={row.id}
        row={row}
        myMembershipId={myMembershipId}
        amSuperadmin={amSuperadmin}
        onChanged={invalidate}
      />
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">1-on-1s</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Check-ins and quarterly conversations — visible only to the two people in them.
          </p>
        </div>
        {canSchedule ? (
          <Button
            onClick={() => {
              setScheduleOpen(true);
            }}
          >
            <Plus className="size-4" />
            Schedule 1-on-1
          </Button>
        ) : null}
      </div>

      {listQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : listQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load 1-on-1s</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {listQuery.error instanceof Error ? listQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <MessageSquare className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No 1-on-1s yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              {canSchedule
                ? 'Schedule a check-in or quarterly conversation with a report.'
                : 'Your manager schedules these — they will appear here.'}
            </p>
          </div>
          {canSchedule ? (
            <Button
              onClick={() => {
                setScheduleOpen(true);
              }}
            >
              <Plus className="size-4" />
              Schedule your first 1-on-1
            </Button>
          ) : null}
        </Card>
      ) : (
        <div className="space-y-6">
          <section className="space-y-3">
            <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Upcoming</h2>
            {upcoming.length === 0 ? (
              <p className="text-[13px] italic text-kw-muted">Nothing scheduled.</p>
            ) : (
              upcoming.map(renderRow)
            )}
          </section>
          {past.length > 0 ? (
            <section className="space-y-3">
              <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Past</h2>
              {past.map(renderRow)}
            </section>
          ) : null}
        </div>
      )}

      <ScheduleModal
        open={scheduleOpen}
        onClose={() => {
          setScheduleOpen(false);
        }}
        onCreated={() => {
          invalidate();
          toast.show('1-on-1 scheduled', { variant: 'success' });
        }}
        myMembershipId={myMembershipId}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// One row + expandable detail
// ─────────────────────────────────────────────────────────────────────────

function OneOnOneCard({
  row,
  myMembershipId,
  amSuperadmin,
  onChanged,
}: {
  row: OneOnOneWithParticipants;
  myMembershipId: string | null;
  amSuperadmin: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);

  const amManager = myMembershipId !== null && row.managerMembershipId === myMembershipId;
  const amReport = myMembershipId !== null && row.reportMembershipId === myMembershipId;
  const isParticipant = amManager || amReport;

  // "The other person" from my seat; superadmin observers see both names.
  const counterpartLabel = amManager
    ? fullName(row.reportMembership)
    : amReport
      ? fullName(row.managerMembership)
      : `${fullName(row.managerMembership)} ↔ ${fullName(row.reportMembership)}`;

  const completeMutation = useMutation({
    mutationFn: () => completeOneOnOne(row.id),
    onSuccess: () => {
      onChanged();
      toast.show('1-on-1 completed', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not complete the 1-on-1.'), { variant: 'danger' });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelOneOnOne(row.id),
    onSuccess: () => {
      onChanged();
      toast.show('1-on-1 canceled', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not cancel the 1-on-1.'), { variant: 'danger' });
    },
  });

  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <button
          type="button"
          aria-label={
            expanded ? `Collapse 1-on-1 with ${counterpartLabel}` : `Expand 1-on-1 with ${counterpartLabel}`
          }
          onClick={() => {
            setExpanded((v) => !v);
          }}
          className="mt-0.5 rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
        >
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium text-kw-shark">{counterpartLabel}</p>
          <p className="mt-0.5 text-[12px] text-kw-muted">{formatWhen(row.scheduledFor)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant={KIND_META[row.kind].variant}>{KIND_META[row.kind].label}</Badge>
          <Badge variant={STATUS_META[row.status].variant}>{STATUS_META[row.status].label}</Badge>
        </div>
      </div>

      {expanded ? (
        <div className="mt-3 space-y-3 border-t border-kw-border pt-3">
          {isConversationKind(row.kind) ? (
            <QuarterlyPanel
              row={row}
              expanded={expanded}
              editable={amManager && row.status !== 'canceled'}
              onChanged={onChanged}
            />
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <NotesSide
              title={`Manager — ${fullName(row.managerMembership)}`}
              row={row}
              side="manager"
              editable={amManager && row.status !== 'canceled'}
              onChanged={onChanged}
            />
            <NotesSide
              title={`Report — ${fullName(row.reportMembership)}`}
              row={row}
              side="report"
              editable={amReport && row.status !== 'canceled'}
              onChanged={onChanged}
            />
          </div>

          {row.status === 'scheduled' && (isParticipant || amSuperadmin) ? (
            <div className="flex justify-end gap-2">
              {isParticipant || amSuperadmin ? (
                <Button
                  size="sm"
                  variant="ghost"
                  isLoading={cancelMutation.isPending}
                  onClick={() => {
                    if (window.confirm('Cancel this 1-on-1?')) cancelMutation.mutate();
                  }}
                >
                  Cancel 1-on-1
                </Button>
              ) : null}
              {isParticipant ? (
                <Button
                  size="sm"
                  isLoading={completeMutation.isPending}
                  onClick={() => {
                    completeMutation.mutate();
                  }}
                >
                  Complete
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/**
 * One side (manager or report) of the conversation: notes + 1-10 rating.
 * Editable ONLY when it is the signed-in participant's own side — the
 * server enforces the same split (`field_not_yours`).
 */
function NotesSide({
  title,
  row,
  side,
  editable,
  onChanged,
}: {
  title: string;
  row: OneOnOneWithParticipants;
  side: 'manager' | 'report';
  editable: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const savedNotes = side === 'manager' ? row.managerNotes : row.reportNotes;
  const savedRating = side === 'manager' ? row.managerRating : row.reportRating;

  const [notes, setNotes] = useState(savedNotes ?? '');
  const [rating, setRating] = useState<number | null>(savedRating);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setNotes(savedNotes ?? '');
    setRating(savedRating);
    setDirty(false);
  }, [savedNotes, savedRating]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateOneOnOne(
        row.id,
        side === 'manager'
          ? { managerNotes: notes.trim() || null, managerRating: rating }
          : { reportNotes: notes.trim() || null, reportRating: rating },
      ),
    onSuccess: () => {
      setDirty(false);
      onChanged();
      toast.show('Your side is saved', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not save your notes.'), { variant: 'danger' });
    },
  });

  return (
    <div className="rounded-kw-md border border-kw-border p-3">
      <p className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">{title}</p>

      {editable ? (
        <textarea
          aria-label={`${title} notes`}
          value={notes}
          maxLength={5000}
          rows={3}
          placeholder="Talking points, wins, concerns…"
          onChange={(e) => {
            setNotes(e.target.value);
            setDirty(true);
          }}
          className={`mt-2 ${TEXTAREA_CLS}`}
        />
      ) : (
        <p className="mt-2 whitespace-pre-wrap text-[13px] text-kw-shark">
          {savedNotes?.trim() ? savedNotes : <span className="italic text-kw-muted">No notes yet.</span>}
        </p>
      )}

      <div className="mt-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-kw-muted">Rating</p>
        {editable ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                aria-label={`Rate ${String(n)} out of 10`}
                aria-pressed={rating === n}
                onClick={() => {
                  setRating(rating === n ? null : n);
                  setDirty(true);
                }}
                className={`size-7 rounded-kw-md border text-[12px] font-medium transition-colors ${
                  rating === n
                    ? 'border-kw-cerulean bg-kw-cerulean text-white'
                    : 'border-kw-border bg-white text-kw-muted hover:bg-kw-bg'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-[13px] text-kw-shark">
            {savedRating === null ? (
              <span className="italic text-kw-muted">Not rated.</span>
            ) : (
              `${String(savedRating)}/10`
            )}
          </p>
        )}
      </div>

      {editable ? (
        <div className="mt-2 flex justify-end">
          <Button
            size="sm"
            variant="secondary"
            isLoading={saveMutation.isPending}
            disabled={!dirty}
            onClick={() => {
              saveMutation.mutate();
            }}
          >
            Save my side
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Quarterly / annual — the structured EOS 5-5-5 panel
// ─────────────────────────────────────────────────────────────────────────

interface DraftCoreValue {
  label: string;
  rating: CoreValueRating | null;
  note: string;
}
interface DraftRole {
  label: string;
  get: boolean;
  want: boolean;
  capacity: boolean;
  note: string;
}
interface DraftRock {
  label: string;
  onTrack: boolean;
  note: string;
}
interface AgendaDraft {
  coreValues: DraftCoreValue[];
  roles: DraftRole[];
  rocks: DraftRock[];
  samePageRating: number | null;
  summary: string;
}

/**
 * Seed the editable draft from the saved agenda when present (the stable
 * snapshot), otherwise from the live context labels. Each section falls
 * back independently so a half-filled agenda still shows fresh context.
 */
function buildAgendaDraft(
  agenda: ConversationAgenda | null,
  context: QuarterlyContext | undefined,
): AgendaDraft {
  const coreValues: DraftCoreValue[] =
    agenda?.coreValues && agenda.coreValues.length > 0
      ? agenda.coreValues.map((c) => ({ label: c.label, rating: c.rating ?? null, note: c.note ?? '' }))
      : (context?.coreValues ?? []).map((label) => ({ label, rating: null, note: '' }));

  const roles: DraftRole[] =
    agenda?.roles && agenda.roles.length > 0
      ? agenda.roles.map((r) => ({
          label: r.label,
          get: r.get ?? false,
          want: r.want ?? false,
          capacity: r.capacity ?? false,
          note: r.note ?? '',
        }))
      : (context?.roles ?? []).map((r) => ({
          label: r.seatName,
          get: false,
          want: false,
          capacity: false,
          note: '',
        }));

  const rocks: DraftRock[] =
    agenda?.rocks && agenda.rocks.length > 0
      ? agenda.rocks.map((r) => ({ label: r.label, onTrack: r.onTrack ?? false, note: r.note ?? '' }))
      : (context?.rocks ?? []).map((r) => ({ label: r.title, onTrack: false, note: '' }));

  return {
    coreValues,
    roles,
    rocks,
    samePageRating: agenda?.samePageRating ?? null,
    summary: agenda?.summary ?? '',
  };
}

function draftToAgenda(draft: AgendaDraft): ConversationAgenda {
  return {
    coreValues: draft.coreValues.map((c) => ({
      label: c.label,
      rating: c.rating,
      note: c.note.trim() || null,
    })),
    roles: draft.roles.map((r) => ({
      label: r.label,
      get: r.get,
      want: r.want,
      capacity: r.capacity,
      note: r.note.trim() || null,
    })),
    rocks: draft.rocks.map((r) => ({
      label: r.label,
      onTrack: r.onTrack,
      note: r.note.trim() || null,
    })),
    samePageRating: draft.samePageRating,
    summary: draft.summary.trim() || null,
  };
}

const SECTION_LABEL_CLS = 'text-[12px] font-medium uppercase tracking-wide text-kw-muted';
const CHECKBOX_CLS = 'size-4 rounded border-kw-border accent-kw-cerulean';
const ITEM_CLS = 'rounded-kw-md border border-kw-border bg-white p-2.5';

/**
 * The structured 5-5-5 conversation: the org's core values, the report's
 * roles + GWC, and their rocks — all derived live — plus a "same page"
 * rating and summary. The manager captures an assessment into `agendaJson`
 * (mirroring {@link NotesSide}: dirty-tracked, one explicit save); everyone
 * else sees it read-only.
 */
function QuarterlyPanel({
  row,
  expanded,
  editable,
  onChanged,
}: {
  row: OneOnOneWithParticipants;
  expanded: boolean;
  editable: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const savedAgenda = row.agendaJson;

  const contextQuery = useQuery({
    queryKey: ['eos', 'one-on-ones', row.id, 'context'],
    queryFn: () => getQuarterlyContext(row.id),
    enabled: expanded,
  });
  const context = contextQuery.data;

  const [draft, setDraft] = useState<AgendaDraft>(() => buildAgendaDraft(savedAgenda, undefined));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(buildAgendaDraft(savedAgenda, context));
    setDirty(false);
  }, [savedAgenda, context]);

  const saveMutation = useMutation({
    mutationFn: () => updateOneOnOne(row.id, { agendaJson: draftToAgenda(draft) }),
    onSuccess: () => {
      setDirty(false);
      onChanged();
      toast.show('Conversation saved', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not save the conversation.'), { variant: 'danger' });
    },
  });

  function patchCoreValue(idx: number, patch: Partial<DraftCoreValue>): void {
    setDraft((d) => ({
      ...d,
      coreValues: d.coreValues.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
    setDirty(true);
  }
  function patchRole(idx: number, patch: Partial<DraftRole>): void {
    setDraft((d) => ({ ...d, roles: d.roles.map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
    setDirty(true);
  }
  function patchRock(idx: number, patch: Partial<DraftRock>): void {
    setDraft((d) => ({ ...d, rocks: d.rocks.map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
    setDirty(true);
  }

  const seatByName = useMemo(() => {
    const map = new Map<string, QuarterlyContext['roles'][number]>();
    for (const r of context?.roles ?? []) map.set(r.seatName, r);
    return map;
  }, [context]);
  const rockByTitle = useMemo(() => {
    const map = new Map<string, QuarterlyContext['rocks'][number]>();
    for (const r of context?.rocks ?? []) map.set(r.title, r);
    return map;
  }, [context]);

  const headline = row.kind === 'annual' ? 'Annual review — 5-5-5' : 'Quarterly conversation — 5-5-5';

  return (
    <div className="space-y-4 rounded-kw-md border border-kw-border bg-kw-bg/40 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13px] font-semibold text-kw-shark">{headline}</p>
        {context ? <Badge variant="info">{context.quarterLabel}</Badge> : null}
      </div>

      {contextQuery.isLoading ? (
        <div className="flex justify-center py-6">
          <div className="size-6 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : contextQuery.error ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
          <p className="text-[12px] text-kw-danger-text">
            {describeEosError(contextQuery.error, 'Could not load the conversation context.')}
          </p>
        </div>
      ) : (
        <>
          {/* ── Core Values ─────────────────────────────────────────── */}
          <section className="space-y-2">
            <p className={SECTION_LABEL_CLS}>Core Values</p>
            {draft.coreValues.length === 0 ? (
              <p className="text-[13px] italic text-kw-muted">No core values defined in the V/TO yet.</p>
            ) : (
              <ul className="space-y-2">
                {draft.coreValues.map((cv, idx) => (
                  <li key={`${String(idx)}-${cv.label}`} className={ITEM_CLS}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-[13px] font-medium text-kw-shark">{cv.label}</span>
                      {editable ? (
                        <div className="flex gap-1">
                          {VALUE_RATINGS.map((r) => (
                            <button
                              key={r.value}
                              type="button"
                              aria-label={`${r.label} — ${cv.label}`}
                              aria-pressed={cv.rating === r.value}
                              onClick={() => {
                                patchCoreValue(idx, { rating: cv.rating === r.value ? null : r.value });
                              }}
                              className={`size-7 rounded-kw-md border text-[13px] font-semibold transition-colors ${
                                cv.rating === r.value
                                  ? 'border-kw-cerulean bg-kw-cerulean text-white'
                                  : 'border-kw-border bg-white text-kw-muted hover:bg-kw-bg'
                              }`}
                            >
                              {r.symbol}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <RatingPill rating={cv.rating} />
                      )}
                    </div>
                    <NoteField
                      label={`Note on ${cv.label}`}
                      value={cv.note}
                      editable={editable}
                      onChange={(v) => {
                        patchCoreValue(idx, { note: v });
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ── Roles & GWC ─────────────────────────────────────────── */}
          <section className="space-y-2">
            <p className={SECTION_LABEL_CLS}>Roles &amp; GWC</p>
            {context?.gwc ? (
              <div className="rounded-kw-md border border-kw-border bg-white p-2.5">
                <p className="text-[11px] text-kw-muted">Latest review — {context.gwc.roundLabel}</p>
                <div className="mt-1.5 grid grid-cols-3 gap-2">
                  <GwcStat label="Gets it" tally={context.gwc.getsIt} />
                  <GwcStat label="Wants it" tally={context.gwc.wantsIt} />
                  <GwcStat label="Capacity" tally={context.gwc.capacity} />
                </div>
              </div>
            ) : (
              <p className="text-[13px] italic text-kw-muted">No completed People Analyzer round yet.</p>
            )}

            {draft.roles.length === 0 ? (
              <p className="text-[13px] italic text-kw-muted">No seats assigned to this person.</p>
            ) : (
              <ul className="space-y-2">
                {draft.roles.map((role, idx) => {
                  const seat = seatByName.get(role.label);
                  return (
                    <li key={`${String(idx)}-${role.label}`} className={ITEM_CLS}>
                      <p className="text-[13px] font-medium text-kw-shark">{role.label}</p>
                      {seat?.purpose ? (
                        <p className="mt-0.5 text-[12px] text-kw-muted">{seat.purpose}</p>
                      ) : null}
                      {seat && seat.responsibilities.length > 0 ? (
                        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[12px] text-kw-shark">
                          {seat.responsibilities.map((resp, ri) => (
                            <li key={`${String(ri)}-${resp}`}>{resp}</li>
                          ))}
                        </ul>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-3">
                        <GwcCheck
                          label="Gets it"
                          checked={role.get}
                          editable={editable}
                          onChange={(v) => {
                            patchRole(idx, { get: v });
                          }}
                        />
                        <GwcCheck
                          label="Wants it"
                          checked={role.want}
                          editable={editable}
                          onChange={(v) => {
                            patchRole(idx, { want: v });
                          }}
                        />
                        <GwcCheck
                          label="Capacity"
                          checked={role.capacity}
                          editable={editable}
                          onChange={(v) => {
                            patchRole(idx, { capacity: v });
                          }}
                        />
                      </div>
                      <NoteField
                        label={`Note on ${role.label}`}
                        value={role.note}
                        editable={editable}
                        onChange={(v) => {
                          patchRole(idx, { note: v });
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* ── Rocks ───────────────────────────────────────────────── */}
          <section className="space-y-2">
            <p className={SECTION_LABEL_CLS}>Rocks</p>
            {draft.rocks.length === 0 ? (
              <p className="text-[13px] italic text-kw-muted">No rocks this quarter.</p>
            ) : (
              <ul className="space-y-2">
                {draft.rocks.map((rock, idx) => {
                  const ctxRock = rockByTitle.get(rock.label);
                  return (
                    <li key={`${String(idx)}-${rock.label}`} className={ITEM_CLS}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[13px] font-medium text-kw-shark">{rock.label}</span>
                        {ctxRock ? (
                          <Badge variant="neutral">{ctxRock.status.replace(/_/g, ' ')}</Badge>
                        ) : null}
                      </div>
                      <div className="mt-2">
                        <GwcCheck
                          label="On track"
                          checked={rock.onTrack}
                          editable={editable}
                          onChange={(v) => {
                            patchRock(idx, { onTrack: v });
                          }}
                        />
                      </div>
                      <NoteField
                        label={`Note on ${rock.label}`}
                        value={rock.note}
                        editable={editable}
                        onChange={(v) => {
                          patchRock(idx, { note: v });
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* ── Same page + summary ─────────────────────────────────── */}
          <section className="space-y-2 border-t border-kw-border pt-3">
            <p className={SECTION_LABEL_CLS}>Same page rating</p>
            {editable ? (
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-label={`Same page ${String(n)} out of 10`}
                    aria-pressed={draft.samePageRating === n}
                    onClick={() => {
                      setDraft((d) => ({ ...d, samePageRating: d.samePageRating === n ? null : n }));
                      setDirty(true);
                    }}
                    className={`size-7 rounded-kw-md border text-[12px] font-medium transition-colors ${
                      draft.samePageRating === n
                        ? 'border-kw-cerulean bg-kw-cerulean text-white'
                        : 'border-kw-border bg-white text-kw-muted hover:bg-kw-bg'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-[13px] text-kw-shark">
                {draft.samePageRating === null ? (
                  <span className="italic text-kw-muted">Not rated.</span>
                ) : (
                  `${String(draft.samePageRating)}/10`
                )}
              </p>
            )}

            <p className={SECTION_LABEL_CLS}>Summary</p>
            {editable ? (
              <textarea
                aria-label="Conversation summary"
                value={draft.summary}
                maxLength={5000}
                rows={3}
                placeholder="Overall takeaways, agreements, next steps…"
                onChange={(e) => {
                  setDraft((d) => ({ ...d, summary: e.target.value }));
                  setDirty(true);
                }}
                className={TEXTAREA_CLS}
              />
            ) : (
              <p className="whitespace-pre-wrap text-[13px] text-kw-shark">
                {draft.summary.trim() ? (
                  draft.summary
                ) : (
                  <span className="italic text-kw-muted">No summary yet.</span>
                )}
              </p>
            )}
          </section>

          {editable ? (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="secondary"
                isLoading={saveMutation.isPending}
                disabled={!dirty}
                onClick={() => {
                  saveMutation.mutate();
                }}
              >
                Save conversation
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function GwcStat({ label, tally }: { label: string; tally: { yes: number; no: number } }) {
  return (
    <div className="rounded-kw-md bg-kw-bg p-2 text-center">
      <p className="text-[11px] font-medium uppercase tracking-wide text-kw-muted">{label}</p>
      <p className="mt-0.5 text-[13px] text-kw-shark">
        {String(tally.yes)} / {String(tally.yes + tally.no)}
      </p>
    </div>
  );
}

function GwcCheck({
  label,
  checked,
  editable,
  onChange,
}: {
  label: string;
  checked: boolean;
  editable: boolean;
  onChange: (v: boolean) => void;
}) {
  if (!editable) {
    return (
      <span className="inline-flex items-center gap-1 text-[12px] text-kw-shark">
        <span aria-hidden className={checked ? 'text-kw-cerulean' : 'text-kw-ghost'}>
          {checked ? '☑' : '☐'}
        </span>
        {label}
      </span>
    );
  }
  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-kw-shark">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
        className={CHECKBOX_CLS}
      />
      {label}
    </label>
  );
}

function RatingPill({ rating }: { rating: CoreValueRating | null }) {
  if (rating === null) return <span className="text-[13px] italic text-kw-muted">—</span>;
  const meta = VALUE_RATINGS.find((r) => r.value === rating);
  if (!meta) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-kw-full bg-kw-bg px-2 py-0.5 text-[12px] font-medium text-kw-shark">
      <span aria-hidden className="text-[13px] font-semibold">
        {meta.symbol}
      </span>
      {meta.label}
    </span>
  );
}

function NoteField({
  label,
  value,
  editable,
  onChange,
}: {
  label: string;
  value: string;
  editable: boolean;
  onChange: (v: string) => void;
}) {
  if (!editable) {
    if (!value.trim()) return null;
    return <p className="mt-1.5 whitespace-pre-wrap text-[12px] text-kw-muted">{value}</p>;
  }
  return (
    <input
      type="text"
      aria-label={label}
      value={value}
      maxLength={1000}
      placeholder="Add a note…"
      onChange={(e) => {
        onChange(e.target.value);
      }}
      className={`mt-1.5 ${TEXTAREA_CLS}`}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Schedule modal (department leads + superadmins)
// ─────────────────────────────────────────────────────────────────────────

function ScheduleModal({
  open,
  onClose,
  onCreated,
  myMembershipId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  myMembershipId: string | null;
}) {
  const [reportMembershipId, setReportMembershipId] = useState('');
  const [kind, setKind] = useState<OneOnOneKind>('check_in');
  const [scheduledFor, setScheduledFor] = useState('');

  const employeesQuery = useQuery({
    queryKey: ['employees', 'directory'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
    enabled: open,
  });
  const reports = useMemo(
    () =>
      (employeesQuery.data?.data ?? []).filter(
        (e) => e.membershipId !== null && e.membershipId !== myMembershipId,
      ),
    [employeesQuery.data, myMembershipId],
  );

  const mutation = useMutation({
    mutationFn: () =>
      createOneOnOne({
        reportMembershipId,
        kind,
        scheduledFor: new Date(scheduledFor).toISOString(),
      }),
    onSuccess: () => {
      onCreated();
      onClose();
      setReportMembershipId('');
      setKind('check_in');
      setScheduledFor('');
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!reportMembershipId || !scheduledFor) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Schedule 1-on-1" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">With</span>
          <select
            value={reportMembershipId}
            required
            onChange={(e) => {
              setReportMembershipId(e.target.value);
            }}
            className={SELECT_CLS}
          >
            <option value="">Pick a report…</option>
            {reports.map((r) => (
              <option key={r.id} value={r.membershipId ?? ''}>
                {r.firstName} {r.lastName}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Kind</span>
            <select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as OneOnOneKind);
              }}
              className={SELECT_CLS}
            >
              <option value="check_in">Check-in</option>
              <option value="quarterly">Quarterly conversation</option>
              <option value="annual">Annual review</option>
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">When</span>
            <input
              type="datetime-local"
              value={scheduledFor}
              required
              onChange={(e) => {
                setScheduledFor(e.target.value);
              }}
              className={SELECT_CLS}
            />
          </label>
        </div>

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {describeEosError(mutation.error, 'Could not schedule the 1-on-1.')}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={mutation.isPending}
            disabled={!reportMembershipId || !scheduledFor}
          >
            Schedule
          </Button>
        </div>
      </form>
    </Modal>
  );
}
